import { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import { managementService } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import 'swiper/css';

const Philosophy = () => {
    const sectionRef = useRef(null);
    const isInView = useInView(sectionRef, { once: true, amount: 0.3 });
    const { language } = useLanguage();
    
    const [introData, setIntroData] = useState(null);
    const [standards, setStandards] = useState([]);

    useEffect(() => {
        fetchData();
    }, [language]);

    const fetchData = async () => {
        try {
            const [intro, standardsData] = await Promise.all([
                managementService.getIntroduction(),
                managementService.getStandards()
            ]);
            setIntroData(intro);
            setStandards(standardsData || []);
        } catch (err) {
            console.error('API error:', err);
        }
    };

    const fadeInUp = {
        hidden: { opacity: 0, y: 60 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    const fadeInLeft = {
        hidden: { opacity: 0, x: -80 },
        visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    const fadeInRight = {
        hidden: { opacity: 0, x: 80 },
        visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.2, delayChildren: 0.3 }
        }
    };

    const title = introData?.title || (language === 'fa' ? 'چیستی و فلسفه‌کاریز' : 'What is Kariz?');
    const description = introData?.description || '';

    return (
        <div ref={sectionRef} id="philosophy" className="max-w-6xl w-full mx-auto px-3 sm:px-4 md:px-0 my-8 sm:my-12 md:my-16">
            <div className="relative w-full flex flex-col md:flex-row-reverse items-center gap-6 sm:gap-8 md:gap-8" dir="rtl">
                {/* Dağ ve Bulutlar */}
                <motion.div
                    variants={fadeInRight}
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                    className="w-full md:w-auto flex justify-center items-center order-1 md:order-2"
                >
                    <div className="relative w-full flex justify-center overflow-hidden">
                        <motion.img src="/mobile-mount.svg" alt="Mountain" className="relative z-10 w-full block md:hidden" />
                        <motion.img src="/mount.png" alt="Mountain" className="relative z-10 w-full max-w-none hidden md:block" />

                        <motion.img src="/cloud-1.svg" alt="Cloud" className="absolute top-8 sm:top-12 z-20 w-14 sm:w-20 md:hidden"
                            animate={{ x: ['120%', '-50%'], opacity: [0, 1, 1, 0.8, 0], scale: [0.8, 1, 1.05, 0.9, 0.7] }}
                            transition={{ x: { duration: 12, repeat: Infinity, ease: "linear" }, opacity: { duration: 12, repeat: Infinity, ease: "easeInOut", times: [0, 0.1, 0.7, 0.9, 1] }, scale: { duration: 12, repeat: Infinity, ease: "easeInOut", times: [0, 0.1, 0.5, 0.8, 1] } }}
                        />
                        <motion.img src="/cloud-2.png" alt="Cloud" className="absolute bottom-10 sm:bottom-16 z-20 w-12 sm:w-16 md:hidden"
                            animate={{ x: ['-50%', '120%'], opacity: [0, 1, 1, 0.7, 0], scale: [0.7, 1, 1.1, 0.85, 0.6] }}
                            transition={{ x: { duration: 14, repeat: Infinity, ease: "linear", delay: 2 }, opacity: { duration: 14, repeat: Infinity, ease: "easeInOut", times: [0, 0.1, 0.7, 0.9, 1], delay: 2 }, scale: { duration: 14, repeat: Infinity, ease: "easeInOut", times: [0, 0.1, 0.5, 0.8, 1], delay: 2 } }}
                        />
                        <motion.img src="/cloud-1.svg" alt="Cloud" className="absolute top-20 sm:top-28 z-10 w-10 sm:w-14 md:hidden"
                            animate={{ x: ['130%', '-60%'], opacity: [0, 0.9, 0.9, 0.6, 0], scale: [0.6, 0.9, 1, 0.8, 0.5] }}
                            transition={{ x: { duration: 10, repeat: Infinity, ease: "linear", delay: 5 }, opacity: { duration: 10, repeat: Infinity, ease: "easeInOut", times: [0, 0.15, 0.65, 0.85, 1], delay: 5 }, scale: { duration: 10, repeat: Infinity, ease: "easeInOut", times: [0, 0.15, 0.5, 0.75, 1], delay: 5 } }}
                        />

                        <motion.img src="/cloud-1.svg" alt="Cloud" className="absolute top-16 z-20 w-auto hidden md:block"
                            animate={{ x: ['110%', '-40%'], opacity: [0, 1, 1, 0.7, 0], scale: [0.9, 1.1, 1.15, 0.95, 0.8] }}
                            transition={{ x: { duration: 15, repeat: Infinity, ease: "linear" }, opacity: { duration: 15, repeat: Infinity, ease: "easeInOut", times: [0, 0.1, 0.7, 0.9, 1] }, scale: { duration: 15, repeat: Infinity, ease: "easeInOut", times: [0, 0.1, 0.5, 0.8, 1] } }}
                        />
                        <motion.img src="/cloud-2.png" alt="Cloud" className="absolute bottom-20 z-20 w-auto hidden md:block"
                            animate={{ x: ['-40%', '110%'], opacity: [0, 1, 1, 0.6, 0], scale: [0.8, 1.1, 1.2, 0.9, 0.7] }}
                            transition={{ x: { duration: 16, repeat: Infinity, ease: "linear", delay: 3 }, opacity: { duration: 16, repeat: Infinity, ease: "easeInOut", times: [0, 0.1, 0.7, 0.9, 1], delay: 3 }, scale: { duration: 16, repeat: Infinity, ease: "easeInOut", times: [0, 0.1, 0.5, 0.8, 1], delay: 3 } }}
                        />
                        <motion.img src="/cloud-1.svg" alt="Cloud" className="absolute top-40 z-10 w-auto hidden md:block opacity-60"
                            animate={{ x: ['120%', '-50%'], opacity: [0, 0.5, 0.5, 0.3, 0], scale: [0.7, 0.8, 0.9, 0.75, 0.6] }}
                            transition={{ x: { duration: 12, repeat: Infinity, ease: "linear", delay: 6 }, opacity: { duration: 12, repeat: Infinity, ease: "easeInOut", times: [0, 0.1, 0.65, 0.85, 1], delay: 6 }, scale: { duration: 12, repeat: Infinity, ease: "easeInOut", times: [0, 0.1, 0.5, 0.75, 1], delay: 6 } }}
                        />
                        <motion.img src="/cloud-2.png" alt="Cloud" className="absolute bottom-32 z-10 w-auto hidden md:block opacity-50"
                            animate={{ x: ['-50%', '130%'], opacity: [0, 0.6, 0.6, 0.4, 0], scale: [0.6, 0.85, 0.95, 0.7, 0.5] }}
                            transition={{ x: { duration: 10, repeat: Infinity, ease: "linear", delay: 8 }, opacity: { duration: 10, repeat: Infinity, ease: "easeInOut", times: [0, 0.15, 0.6, 0.8, 1], delay: 8 }, scale: { duration: 10, repeat: Infinity, ease: "easeInOut", times: [0, 0.15, 0.5, 0.75, 1], delay: 8 } }}
                        />
                    </div>
                </motion.div>

                {/* Metin ve Başlık */}
                <motion.div
                    variants={fadeInLeft}
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                    className="w-full md:w-[684px] text-right z-20 order-2 md:order-1"
                >
                    <motion.h2
                        variants={fadeInUp}
                        className="text-lg sm:text-xl md:text-2xl lg:text-4xl font-bold text-[#292DD8] mb-3 sm:mb-4 md:mb-6 text-center md:text-right"
                    >
                        {title}
                    </motion.h2>

                    <motion.p
                        variants={fadeInUp}
                        className="text-[#000377] text-justify text-sm sm:text-base md:text-2xl leading-relaxed px-2 sm:px-0"
                    >
                        {description}
                    </motion.p>

                    {/* Standards - Slider */}
                    {standards.length > 0 && (
                        <motion.div
                            variants={staggerContainer}
                            initial="hidden"
                            animate={isInView ? "visible" : "hidden"}
                            className="mt-4 sm:mt-6 md:mt-8"
                        >
                            <Swiper
                                modules={[Autoplay]}
                                slidesPerView={8}
                                spaceBetween={8}
                                loop={true}
                                autoplay={{ delay: 3000, disableOnInteraction: false }}
                                breakpoints={{
                                    320: { slidesPerView: 3, spaceBetween: 8 },
                                    480: { slidesPerView: 4, spaceBetween: 12 },
                                    640: { slidesPerView: 5, spaceBetween: 16 },
                                    768: { slidesPerView: 6, spaceBetween: 20 },
                                    1024: { slidesPerView: 8, spaceBetween: 24 },
                                }}
                                className="standardsSwiper"
                            >
                                {standards.map((standard, i) => (
                                    <SwiperSlide key={i} className="flex items-center justify-center py-2">
                                        <motion.a
                                            href={standard.url || '#'}
                                            target={standard.url ? "_blank" : "_self"}
                                            rel="noopener noreferrer"
                                            whileHover={{ scale: 1.1, rotate: 5 }}
                                            className="cursor-pointer"
                                        >
                                            <img
                                                src={standard.image}
                                                alt={`Standard ${i + 1}`}
                                                className="w-8 h-8 sm:w-12 sm:h-12 md:w-24 md:h-24 object-contain"
                                            />
                                        </motion.a>
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        </motion.div>
                    )}
                </motion.div>
            </div>
        </div>
    );
};

export default Philosophy;